/**
 * Hierarchical Resume Storage System
 * Enhanced Segmentation Agent v2.0
 * 
 * FINAL SECURITY HARDENING - SECURE RESUME STORAGE
 * 
 * IndexedDB-based storage for resume sessions with book/chapter/witness hierarchy
 * Enhanced with cryptographic security, ownership validation, and tamper detection
 * Supports 30-day retention, automatic cleanup, and efficient querying
 * 
 * Security Features:
 * - Cryptographic validation of resume sessions
 * - Ownership verification for session recovery
 * - Tamper detection for session integrity
 * - Secure session isolation between witnesses
 */

import { 
  BookResumeData, 
  ChapterResumeData, 
  WitnessResumeSession, 
  BatchResumeSession,
  ResumeStorageSchema,
  ResumeEvent,
  ResumeSessionMetrics
} from '../types/resumeTypes'
import { instrumentIndexedDBWriteTransactions } from './indexedDBSnapshotGuard'
import {
  assertLedgerSafePostgresPrimaryOperationSupported,
  indexedDbKeyFromRecord,
  runLedgerSafePostgresPrimaryWrite,
} from './ledgerSafePostgresPrimaryWriteBridge'

// ============================================================================
// Storage Configuration
// ============================================================================

export const RESUME_DB_NAME = 'SegmentationResumeDB'
export const RESUME_DB_VERSION = 1
const DB_NAME = RESUME_DB_NAME
const DB_VERSION = RESUME_DB_VERSION
const EXPIRY_DAYS = 30
const CLEANUP_INTERVAL_HOURS = 6
export const RESUME_DB_STORE_NAMES = [
  'book_resume_data',
  'witness_sessions',
  'batch_sessions',
  'resume_metadata',
  'resume_events'
] as const

export type ResumeDbStoreName = typeof RESUME_DB_STORE_NAMES[number]

export const RESUME_DB_STORE_KEY_PATHS: Record<ResumeDbStoreName, string | readonly string[]> = {
  book_resume_data: 'book_id',
  witness_sessions: 'persistent_id',
  batch_sessions: 'persistent_id',
  resume_metadata: 'key',
  resume_events: ['sessionId', 'timestamp'],
}

interface ResumeStorageConfig {
  dbName: string
  version: number
  expiryDays: number
  maxStorageSize: number // MB
  compressionEnabled: boolean
  securityEnabled: boolean
  ownershipValidation: boolean
  tamperDetection: boolean
}

interface SecureResumeSession {
  session_hash: string
  ownership_context: {
    book_id: string
    chapter_id: string
    witness_id: string
  }
  security_signature: string
  integrity_checksum: string
  created_timestamp: number
  last_verified: number
}

const DEFAULT_CONFIG: ResumeStorageConfig = {
  dbName: DB_NAME,
  version: DB_VERSION,
  expiryDays: EXPIRY_DAYS,
  maxStorageSize: 100, // 100MB limit
  compressionEnabled: true,
  securityEnabled: true,
  ownershipValidation: true,
  tamperDetection: true
}

const isTestEnvironment = typeof process !== 'undefined' && process.env.NODE_ENV === 'test'

// ============================================================================
// Main Storage Class
// ============================================================================

export class HierarchicalResumeStorage {
  private db: IDBDatabase | null = null
  private config: ResumeStorageConfig
  private isInitialized = false
  private cleanupInterval: NodeJS.Timeout | null = null

  constructor(config: Partial<ResumeStorageConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config }
    if (!isTestEnvironment) {
      this.startPeriodicCleanup()
    }
  }

  // ============================================================================
  // Database Initialization
  // ============================================================================

  async initialize(): Promise<void> {
    if (this.isInitialized && this.db) return

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.config.dbName, this.config.version)

      request.onerror = () => {
        reject(new Error(`Failed to open IndexedDB: ${request.error?.message}`))
      }

      request.onsuccess = () => {
        this.db = request.result
        instrumentIndexedDBWriteTransactions(this.db, this.config.dbName)
        this.isInitialized = true
        if (!isTestEnvironment) {
          console.log('Resume storage initialized successfully')
        }
        resolve()
      }

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result
        this.createStores(db)
      }
    })
  }

  private createStores(db: IDBDatabase): void {
    // Book resume data store
    if (!db.objectStoreNames.contains('book_resume_data')) {
      const bookStore = db.createObjectStore('book_resume_data', { keyPath: 'book_id' })
      bookStore.createIndex('last_activity', 'last_activity')
      bookStore.createIndex('total_resumable_sessions', 'total_resumable_sessions')
    }

    // Witness sessions store
    if (!db.objectStoreNames.contains('witness_sessions')) {
      const witnessStore = db.createObjectStore('witness_sessions', { keyPath: 'persistent_id' })
      witnessStore.createIndex('witness_id', 'witness_id', { unique: false })
      witnessStore.createIndex('book_id', 'book_id', { unique: false })
      witnessStore.createIndex('chapter_id', 'chapter_id', { unique: false })
      witnessStore.createIndex('expires_at', 'expires_at', { unique: false })
      witnessStore.createIndex('can_resume', 'can_resume', { unique: false })
      witnessStore.createIndex('completion_percentage', 'completion_percentage', { unique: false })
      witnessStore.createIndex('last_activity', 'last_activity', { unique: false })
    }

    // Batch sessions store
    if (!db.objectStoreNames.contains('batch_sessions')) {
      const batchStore = db.createObjectStore('batch_sessions', { keyPath: 'persistent_id' })
      batchStore.createIndex('batch_id', 'batch_id', { unique: false })
      batchStore.createIndex('book_id', 'book_id', { unique: false })
      batchStore.createIndex('chapter_id', 'chapter_id', { unique: false })
      batchStore.createIndex('expires_at', 'expires_at', { unique: false })
      batchStore.createIndex('total_files', 'total_files', { unique: false })
      batchStore.createIndex('last_activity', 'last_activity', { unique: false })
    }

    // Resume metadata store
    if (!db.objectStoreNames.contains('resume_metadata')) {
      db.createObjectStore('resume_metadata', { keyPath: 'key' })
    }

    // Events store for analytics
    if (!db.objectStoreNames.contains('resume_events')) {
      const eventsStore = db.createObjectStore('resume_events', { keyPath: ['sessionId', 'timestamp'] })
      eventsStore.createIndex('type', 'type', { unique: false })
      eventsStore.createIndex('timestamp', 'timestamp', { unique: false })
      eventsStore.createIndex('sessionId', 'sessionId', { unique: false })
    }
  }

  // ============================================================================
  // Book-Level Operations
  // ============================================================================

  async getBookResumeData(bookId: string): Promise<BookResumeData | null> {
    await this.initialize()
    if (!this.db) throw new Error('Database not initialized')

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['book_resume_data'], 'readonly')
      const store = transaction.objectStore('book_resume_data')
      const request = store.get(bookId)

      request.onsuccess = () => {
        resolve(request.result || null)
      }

      request.onerror = () => {
        reject(new Error(`Failed to get book resume data: ${request.error?.message}`))
      }
    })
  }

  async saveBookResumeData(bookData: BookResumeData): Promise<void> {
    await this.initialize()
    if (!this.db) throw new Error('Database not initialized')

    return runLedgerSafePostgresPrimaryWrite({
      mutations: [{
        action: 'put',
        databaseName: RESUME_DB_NAME,
        storeName: 'book_resume_data',
        key: bookData.book_id,
        value: bookData,
      }],
      applyCache: () => new Promise<void>((resolve, reject) => {
        const transaction = this.db!.transaction(['book_resume_data'], 'readwrite')
        const store = transaction.objectStore('book_resume_data')
        store.put(bookData)
        transaction.oncomplete = () => {
          void this.updateMetadata().catch(error => {
            console.error('Resume metadata update failed:', error)
          })
          resolve()
        }
        transaction.onerror = () => reject(
          transaction.error || new Error('Failed to save book resume data'),
        )
        transaction.onabort = () => reject(
          transaction.error || new Error('Book resume transaction aborted'),
        )
      }),
    })
  }

  async getAllBooksWithResumeSessions(): Promise<BookResumeData[]> {
    await this.initialize()
    if (!this.db) throw new Error('Database not initialized')

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['book_resume_data'], 'readonly')
      const store = transaction.objectStore('book_resume_data')
      const request = store.getAll()

      request.onsuccess = () => {
        const books = request.result.filter(book => book.total_resumable_sessions > 0)
        resolve(books)
      }

      request.onerror = () => {
        reject(new Error(`Failed to get books with resume sessions: ${request.error?.message}`))
      }
    })
  }

  // ============================================================================
  // Chapter-Level Operations
  // ============================================================================

  async getChapterResumeData(chapterId: string): Promise<ChapterResumeData | null> {
    // Get chapter data from the book's resume data
    const witnessSessions = await this.getWitnessSessionsByChapter(chapterId)
    const batchSessions = await this.getBatchSessionsByChapter(chapterId)

    if (witnessSessions.length === 0 && batchSessions.length === 0) {
      return null
    }

    // Build chapter resume data
    const chapterData: ChapterResumeData = {
      chapter_id: chapterId,
      chapter_name: '', // Will be populated by caller
      witness_sessions: {},
      batch_sessions: {},
      last_activity: 0,
      total_resumable_sessions: 0,
      total_completion_percentage: 0
    }

    // Populate witness sessions
    witnessSessions.forEach(session => {
      chapterData.witness_sessions[session.witness_id] = session
      if (session.last_activity > chapterData.last_activity) {
        chapterData.last_activity = session.last_activity
      }
    })

    // Populate batch sessions
    batchSessions.forEach(session => {
      chapterData.batch_sessions[session.batch_id] = session
      if (session.last_activity > chapterData.last_activity) {
        chapterData.last_activity = session.last_activity
      }
    })

    chapterData.total_resumable_sessions = witnessSessions.length + batchSessions.length

    // Calculate average completion percentage
    const allSessions = [...witnessSessions, ...batchSessions]
    if (allSessions.length > 0) {
      const totalCompletion = allSessions.reduce((sum, session) => {
        // Check if it's a WitnessResumeSession (has completion_percentage)
        if ('completion_percentage' in session) {
          return sum + (session.completion_percentage || 0)
        }
        // For BatchResumeSession, calculate completion from files
        if ('total_files' in session && 'completed_files' in session) {
          const completion = session.total_files > 0 ? (session.completed_files / session.total_files) * 100 : 0
          return sum + completion
        }
        return sum
      }, 0)
      chapterData.total_completion_percentage = totalCompletion / allSessions.length
    }

    return chapterData
  }

  // ============================================================================
  // Witness Session Operations
  // ============================================================================

  async getWitnessResumeSession(witnessId: string): Promise<WitnessResumeSession | null> {
    await this.initialize()
    if (!this.db) throw new Error('Database not initialized')

    return new Promise(async (resolve, reject) => {
      const transaction = this.db!.transaction(['witness_sessions'], 'readonly')
      const store = transaction.objectStore('witness_sessions')
      const index = store.index('witness_id')
      const request = index.get(witnessId)

      request.onsuccess = async () => {
        const session = request.result
        if (!session) {
          resolve(null)
          return
        }

        // SECURITY ENHANCEMENT: Validate session integrity
        if (this.config.securityEnabled) {
          try {
            const isValid = await this.validateSessionIntegrity(session)
            if (!isValid) {
              console.error(`SECURITY VIOLATION: Session integrity check failed for witness ${witnessId}`)
              reject(new Error(`Security violation: Session integrity compromised`))
              return
            }
            console.log(`SECURITY: Session integrity verified for witness ${witnessId}`)
          } catch (error) {
            console.error(`SECURITY ERROR: Session validation failed:`, error)
            reject(new Error(`Security validation failed: ${error}`))
            return
          }
        }

        resolve(session)
      }

      request.onerror = () => {
        reject(new Error(`Failed to get witness resume session: ${request.error?.message}`))
      }
    })
  }

  async saveWitnessResumeSession(session: WitnessResumeSession): Promise<void> {
    await this.initialize()
    if (!this.db) throw new Error('Database not initialized')

    // SECURITY ENHANCEMENT: Validate ownership and generate security metadata
    if (this.config.securityEnabled) {
      await this.validateSessionOwnership(session)
      await this.addSecurityMetadata(session)
    }

    // Update timestamps
    session.last_activity = Date.now()
    if (!session.expires_at) {
      session.expires_at = Date.now() + (this.config.expiryDays * 24 * 60 * 60 * 1000)
    }

    return runLedgerSafePostgresPrimaryWrite({
      mutations: [{
        action: 'put',
        databaseName: RESUME_DB_NAME,
        storeName: 'witness_sessions',
        key: session.persistent_id,
        value: session,
      }],
      applyCache: () => new Promise<void>((resolve, reject) => {
        const transaction = this.db!.transaction(['witness_sessions'], 'readwrite')
        const store = transaction.objectStore('witness_sessions')
        store.put(session)
        transaction.oncomplete = () => {
          void this.updateBookResumeDataForWitness(session)
          void this.logEvent({
            type: 'session_updated',
            sessionId: session.persistent_id,
            timestamp: Date.now(),
            metadata: { witnessId: session.witness_id, completion: session.completion_percentage }
          }).catch(error => {
            console.error('Resume session event write failed:', error)
          })
          console.log(`SECURITY: Witness resume session saved with security validation: ${session.persistent_id}`)
          resolve()
        }
        transaction.onerror = () => reject(
          transaction.error || new Error('Failed to save witness resume session'),
        )
        transaction.onabort = () => reject(
          transaction.error || new Error('Witness resume transaction aborted'),
        )
      }),
    })
  }

  async deleteWitnessResumeSession(sessionId: string): Promise<void> {
    await this.initialize()
    if (!this.db) throw new Error('Database not initialized')

    const session = await this.getWitnessResumeSessionById(sessionId)
    if (!session) return

    return runLedgerSafePostgresPrimaryWrite({
      mutations: [{
        action: 'delete',
        databaseName: RESUME_DB_NAME,
        storeName: 'witness_sessions',
        key: sessionId,
      }],
      applyCache: () => new Promise<void>((resolve, reject) => {
        const transaction = this.db!.transaction(['witness_sessions'], 'readwrite')
        const store = transaction.objectStore('witness_sessions')
        store.delete(sessionId)
        transaction.oncomplete = () => {
          void this.updateBookResumeDataAfterDeletion(session.book_id)
          void this.logEvent({
            type: 'session_deleted',
            sessionId: sessionId,
            timestamp: Date.now()
          }).catch(error => {
            console.error('Resume session deletion event write failed:', error)
          })
          resolve()
        }
        transaction.onerror = () => reject(
          transaction.error || new Error('Failed to delete witness resume session'),
        )
        transaction.onabort = () => reject(
          transaction.error || new Error('Witness resume delete aborted'),
        )
      }),
    })
  }

  async getWitnessSessionsByChapter(chapterId: string): Promise<WitnessResumeSession[]> {
    await this.initialize()
    if (!this.db) throw new Error('Database not initialized')

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['witness_sessions'], 'readonly')
      const store = transaction.objectStore('witness_sessions')
      const index = store.index('chapter_id')
      const request = index.getAll(chapterId)

      request.onsuccess = () => {
        resolve(request.result)
      }

      request.onerror = () => {
        reject(new Error(`Failed to get witness sessions by chapter: ${request.error?.message}`))
      }
    })
  }

  async getWitnessSessionsByBook(bookId: string): Promise<WitnessResumeSession[]> {
    await this.initialize()
    if (!this.db) throw new Error('Database not initialized')

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['witness_sessions'], 'readonly')
      const store = transaction.objectStore('witness_sessions')
      const index = store.index('book_id')
      const request = index.getAll(bookId)

      request.onsuccess = () => {
        resolve(request.result)
      }

      request.onerror = () => {
        reject(new Error(`Failed to get witness sessions by book: ${request.error?.message}`))
      }
    })
  }

  // ============================================================================
  // Batch Session Operations
  // ============================================================================

  async getBatchResumeSession(batchId: string): Promise<BatchResumeSession | null> {
    await this.initialize()
    if (!this.db) throw new Error('Database not initialized')

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['batch_sessions'], 'readonly')
      const store = transaction.objectStore('batch_sessions')
      const index = store.index('batch_id')
      const request = index.get(batchId)

      request.onsuccess = () => {
        resolve(request.result || null)
      }

      request.onerror = () => {
        reject(new Error(`Failed to get batch resume session: ${request.error?.message}`))
      }
    })
  }

  async saveBatchResumeSession(session: BatchResumeSession): Promise<void> {
    await this.initialize()
    if (!this.db) throw new Error('Database not initialized')

    // Update timestamps
    session.last_activity = Date.now()
    if (!session.expires_at) {
      session.expires_at = Date.now() + (this.config.expiryDays * 24 * 60 * 60 * 1000)
    }

    return runLedgerSafePostgresPrimaryWrite({
      mutations: [{
        action: 'put',
        databaseName: RESUME_DB_NAME,
        storeName: 'batch_sessions',
        key: session.persistent_id,
        value: session,
      }],
      applyCache: () => new Promise<void>((resolve, reject) => {
        const transaction = this.db!.transaction(['batch_sessions'], 'readwrite')
        const store = transaction.objectStore('batch_sessions')
        store.put(session)
        transaction.oncomplete = () => {
          void this.logEvent({
            type: 'session_updated',
            sessionId: session.persistent_id,
            timestamp: Date.now(),
            metadata: { batchId: session.batch_id, totalFiles: session.total_files }
          }).catch(error => {
            console.error('Batch resume event write failed:', error)
          })
          resolve()
        }
        transaction.onerror = () => reject(
          transaction.error || new Error('Failed to save batch resume session'),
        )
        transaction.onabort = () => reject(
          transaction.error || new Error('Batch resume transaction aborted'),
        )
      }),
    })
  }

  async getBatchSessionsByChapter(chapterId: string): Promise<BatchResumeSession[]> {
    await this.initialize()
    if (!this.db) throw new Error('Database not initialized')

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['batch_sessions'], 'readonly')
      const store = transaction.objectStore('batch_sessions')
      const index = store.index('chapter_id')
      const request = index.getAll(chapterId)

      request.onsuccess = () => {
        resolve(request.result)
      }

      request.onerror = () => {
        reject(new Error(`Failed to get batch sessions by chapter: ${request.error?.message}`))
      }
    })
  }

  async deleteBatchResumeSession(sessionId: string): Promise<void> {
    await this.initialize()
    if (!this.db) throw new Error('Database not initialized')

    const session = await this.getBatchResumeSessionById(sessionId)
    if (!session) return

    return runLedgerSafePostgresPrimaryWrite({
      mutations: [{
        action: 'delete',
        databaseName: RESUME_DB_NAME,
        storeName: 'batch_sessions',
        key: sessionId,
      }],
      applyCache: () => new Promise<void>((resolve, reject) => {
        const transaction = this.db!.transaction(['batch_sessions'], 'readwrite')
        const store = transaction.objectStore('batch_sessions')
        store.delete(sessionId)
        transaction.oncomplete = () => {
          void this.logEvent({
            type: 'session_deleted',
            sessionId: sessionId,
            timestamp: Date.now()
          }).catch(error => {
            console.error('Batch resume deletion event write failed:', error)
          })
          resolve()
        }
        transaction.onerror = () => reject(
          transaction.error || new Error('Failed to delete batch resume session'),
        )
        transaction.onabort = () => reject(
          transaction.error || new Error('Batch resume delete aborted'),
        )
      }),
    })
  }

  private async getBatchResumeSessionById(sessionId: string): Promise<BatchResumeSession | null> {
    await this.initialize()
    if (!this.db) throw new Error('Database not initialized')

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['batch_sessions'], 'readonly')
      const store = transaction.objectStore('batch_sessions')
      const request = store.get(sessionId)

      request.onsuccess = () => {
        resolve(request.result || null)
      }

      request.onerror = () => {
        reject(new Error(`Failed to get batch session by ID: ${request.error?.message}`))
      }
    })
  }

  // ============================================================================
  // Cleanup and Maintenance
  // ============================================================================

  async cleanupExpiredSessions(): Promise<void> {
    await this.initialize()
    if (!this.db) throw new Error('Database not initialized')

    const now = Date.now()
    const readExpired = <T extends WitnessResumeSession | BatchResumeSession>(
      storeName: 'witness_sessions' | 'batch_sessions',
    ): Promise<T[]> => {
      const transaction = this.db!.transaction([storeName], 'readonly')
      const request = transaction.objectStore(storeName)
        .index('expires_at')
        .getAll(IDBKeyRange.upperBound(now))
      return new Promise((resolve, reject) => {
        request.onsuccess = () => resolve(request.result as T[])
        request.onerror = () => reject(
          request.error || new Error(`Failed to inspect ${storeName}`),
        )
      })
    }
    const [expiredWitnesses, expiredBatches] = await Promise.all([
      readExpired<WitnessResumeSession>('witness_sessions'),
      readExpired<BatchResumeSession>('batch_sessions'),
    ])
    const metadata = this.createMetadata()
    await runLedgerSafePostgresPrimaryWrite({
      mutations: [
        ...expiredWitnesses.map(session => ({
          action: 'delete' as const,
          databaseName: RESUME_DB_NAME,
          storeName: 'witness_sessions',
          key: session.persistent_id,
        })),
        ...expiredBatches.map(session => ({
          action: 'delete' as const,
          databaseName: RESUME_DB_NAME,
          storeName: 'batch_sessions',
          key: session.persistent_id,
        })),
        {
          action: 'put',
          databaseName: RESUME_DB_NAME,
          storeName: 'resume_metadata',
          key: metadata.key,
          value: metadata,
        },
      ],
      applyCache: () => new Promise<void>((resolve, reject) => {
        const transaction = this.db!.transaction([
          'witness_sessions',
          'batch_sessions',
          'resume_metadata',
        ], 'readwrite')
        const witnessStore = transaction.objectStore('witness_sessions')
        expiredWitnesses.forEach(
          session => witnessStore.delete(session.persistent_id),
        )
        const batchStore = transaction.objectStore('batch_sessions')
        expiredBatches.forEach(
          session => batchStore.delete(session.persistent_id),
        )
        transaction.objectStore('resume_metadata').put(metadata)
        transaction.oncomplete = () => resolve()
        transaction.onerror = () => reject(
          transaction.error || new Error('Resume cleanup failed'),
        )
        transaction.onabort = () => reject(
          transaction.error || new Error('Resume cleanup aborted'),
        )
      }),
    })

    console.log(
      `Cleaned up ${expiredWitnesses.length + expiredBatches.length} expired resume sessions`,
    )
  }

  async getExpiringSessions(daysUntilExpiry: number = 3): Promise<(WitnessResumeSession | BatchResumeSession)[]> {
    await this.initialize()
    if (!this.db) throw new Error('Database not initialized')

    const cutoffDate = Date.now() + (daysUntilExpiry * 24 * 60 * 60 * 1000)
    const expiringSessions: (WitnessResumeSession | BatchResumeSession)[] = []

    // Get expiring witness sessions
    const witnessTransaction = this.db.transaction(['witness_sessions'], 'readonly')
    const witnessStore = witnessTransaction.objectStore('witness_sessions')
    const witnessIndex = witnessStore.index('expires_at')
    const witnessRequest = witnessIndex.getAll(IDBKeyRange.upperBound(cutoffDate))

    const witnessResults = await new Promise<WitnessResumeSession[]>((resolve, reject) => {
      witnessRequest.onsuccess = () => resolve(witnessRequest.result)
      witnessRequest.onerror = () => reject(witnessRequest.error)
    })

    // Get expiring batch sessions
    const batchTransaction = this.db.transaction(['batch_sessions'], 'readonly')
    const batchStore = batchTransaction.objectStore('batch_sessions')
    const batchIndex = batchStore.index('expires_at')
    const batchRequest = batchIndex.getAll(IDBKeyRange.upperBound(cutoffDate))

    const batchResults = await new Promise<BatchResumeSession[]>((resolve, reject) => {
      batchRequest.onsuccess = () => resolve(batchRequest.result)
      batchRequest.onerror = () => reject(batchRequest.error)
    })

    return [...witnessResults, ...batchResults]
  }

  // ============================================================================
  // Security Enhancement Methods
  // ============================================================================

  /**
   * Validate session ownership to prevent cross-witness contamination
   */
  private async validateSessionOwnership(session: WitnessResumeSession | BatchResumeSession): Promise<void> {
    if (!this.config.ownershipValidation) return

    // Validate required ownership fields
    if (!session.book_id || !session.chapter_id) {
      throw new Error(`Security violation: Session missing ownership context`)
    }

    // For witness sessions, validate witness_id
    if ('witness_id' in session && !session.witness_id) {
      throw new Error(`Security violation: Witness session missing witness_id`)
    }

    // For batch sessions, validate batch_id
    if ('batch_id' in session && !session.batch_id) {
      throw new Error(`Security violation: Batch session missing batch_id`)
    }

    console.log(`SECURITY: Session ownership validated for ${session.persistent_id}`)
  }

  /**
   * Add security metadata to session
   */
  private async addSecurityMetadata(session: WitnessResumeSession | BatchResumeSession): Promise<void> {
    if (!this.config.securityEnabled) return

    try {
      // Generate session hash for integrity checking
      const sessionData = JSON.stringify({
        persistent_id: session.persistent_id,
        book_id: session.book_id,
        chapter_id: session.chapter_id,
        witness_id: 'witness_id' in session ? session.witness_id : null,
        batch_id: 'batch_id' in session ? session.batch_id : null,
        created_at: session.created_at
      })
      
      const encoder = new TextEncoder()
      const data = encoder.encode(sessionData)
      const hashBuffer = await crypto.subtle.digest('SHA-256', data)
      const hashArray = Array.from(new Uint8Array(hashBuffer))
      const sessionHash = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
      
      // Add security metadata to session
      ;(session as any).security_metadata = {
        session_hash: sessionHash,
        ownership_context: {
          book_id: session.book_id,
          chapter_id: session.chapter_id,
          witness_id: 'witness_id' in session ? session.witness_id : null,
          batch_id: 'batch_id' in session ? session.batch_id : null
        },
        security_signature: sessionHash, // Simple signature for now
        integrity_checksum: sessionHash,
        created_timestamp: Date.now(),
        last_verified: Date.now()
      }
      
      console.log(`SECURITY: Security metadata added to session ${session.persistent_id}`)
    } catch (error) {
      console.error(`SECURITY ERROR: Failed to add security metadata:`, error)
      throw new Error(`Security metadata generation failed: ${error}`)
    }
  }

  /**
   * Validate session integrity
   */
  private async validateSessionIntegrity(session: WitnessResumeSession | BatchResumeSession): Promise<boolean> {
    if (!this.config.tamperDetection) return true

    try {
      const securityMetadata = (session as any).security_metadata
      if (!securityMetadata) {
        console.warn(`SECURITY WARNING: Session ${session.persistent_id} missing security metadata`)
        return true // Allow legacy sessions without security metadata
      }

      // Regenerate session hash
      const sessionData = JSON.stringify({
        persistent_id: session.persistent_id,
        book_id: session.book_id,
        chapter_id: session.chapter_id,
        witness_id: 'witness_id' in session ? session.witness_id : null,
        batch_id: 'batch_id' in session ? session.batch_id : null,
        created_at: session.created_at
      })
      
      const encoder = new TextEncoder()
      const data = encoder.encode(sessionData)
      const hashBuffer = await crypto.subtle.digest('SHA-256', data)
      const hashArray = Array.from(new Uint8Array(hashBuffer))
      const currentHash = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
      
      // Compare with stored hash
      if (currentHash !== securityMetadata.session_hash) {
        console.error(`SECURITY VIOLATION: Session ${session.persistent_id} integrity check failed - hash mismatch`)
        return false
      }
      
      // Validate ownership context
      const storedContext = securityMetadata.ownership_context
      if (storedContext.book_id !== session.book_id || 
          storedContext.chapter_id !== session.chapter_id) {
        console.error(`SECURITY VIOLATION: Session ${session.persistent_id} ownership context mismatch`)
        return false
      }
      
      // Update last verified timestamp
      securityMetadata.last_verified = Date.now()
      
      return true
    } catch (error) {
      console.error(`SECURITY ERROR: Session integrity validation failed:`, error)
      return false
    }
  }

  /**
   * Generate security report for resume sessions
   */
  async generateSecurityReport(): Promise<{
    total_sessions: number
    secure_sessions: number
    legacy_sessions: number
    integrity_violations: number
    ownership_violations: number
    recommendations: string[]
  }> {
    await this.initialize()
    if (!this.db) throw new Error('Database not initialized')

    const allWitnessSessions = await this.getAllWitnessSessions()
    const allBatchSessions = await this.getAllBatchSessions()
    const allSessions = [...allWitnessSessions, ...allBatchSessions]

    let secureSessions = 0
    let legacySessions = 0
    let integrityViolations = 0
    let ownershipViolations = 0

    for (const session of allSessions) {
      const securityMetadata = (session as any).security_metadata
      if (securityMetadata) {
        secureSessions++
        
        // Check integrity
        const isIntegrityValid = await this.validateSessionIntegrity(session)
        if (!isIntegrityValid) {
          integrityViolations++
        }
        
        // Check ownership
        try {
          await this.validateSessionOwnership(session)
        } catch (error) {
          ownershipViolations++
        }
      } else {
        legacySessions++
      }
    }

    const recommendations: string[] = []
    if (legacySessions > 0) {
      recommendations.push(`${legacySessions} legacy sessions without security metadata detected - consider migration`)
    }
    if (integrityViolations > 0) {
      recommendations.push(`${integrityViolations} integrity violations detected - investigate data corruption`)
    }
    if (ownershipViolations > 0) {
      recommendations.push(`${ownershipViolations} ownership violations detected - review access controls`)
    }
    if (allSessions.length === 0) {
      recommendations.push('No resume sessions found - system appears clean')
    }

    return {
      total_sessions: allSessions.length,
      secure_sessions: secureSessions,
      legacy_sessions: legacySessions,
      integrity_violations: integrityViolations,
      ownership_violations: ownershipViolations,
      recommendations
    }
  }

  /**
   * Perform security maintenance on resume sessions
   */
  async performSecurityMaintenance(): Promise<{
    sessions_validated: number
    violations_found: number
    legacy_sessions_upgraded: number
  }> {
    console.log('SECURITY: Performing resume session security maintenance')
    
    const allWitnessSessions = await this.getAllWitnessSessions()
    const allBatchSessions = await this.getAllBatchSessions()
    const allSessions = [...allWitnessSessions, ...allBatchSessions]
    
    let sessionsValidated = 0
    let violationsFound = 0
    let legacySessionsUpgraded = 0
    
    for (const session of allSessions) {
      try {
        // Validate existing sessions
        const isValid = await this.validateSessionIntegrity(session)
        if (!isValid) {
          violationsFound++
        }
        sessionsValidated++
        
        // Upgrade legacy sessions
        const securityMetadata = (session as any).security_metadata
        if (!securityMetadata) {
          await this.addSecurityMetadata(session)
          legacySessionsUpgraded++
        }
      } catch (error) {
        console.error(`SECURITY ERROR: Maintenance failed for session ${session.persistent_id}:`, error)
        violationsFound++
      }
    }
    
    console.log(`SECURITY: Maintenance completed - validated ${sessionsValidated} sessions, found ${violationsFound} violations, upgraded ${legacySessionsUpgraded} legacy sessions`)
    
    return {
      sessions_validated: sessionsValidated,
      violations_found: violationsFound,
      legacy_sessions_upgraded: legacySessionsUpgraded
    }
  }

  // ============================================================================
  // Helper Methods
  // ============================================================================

  private async getWitnessResumeSessionById(sessionId: string): Promise<WitnessResumeSession | null> {
    await this.initialize()
    if (!this.db) throw new Error('Database not initialized')

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['witness_sessions'], 'readonly')
      const store = transaction.objectStore('witness_sessions')
      const request = store.get(sessionId)

      request.onsuccess = () => {
        resolve(request.result || null)
      }

      request.onerror = () => {
        reject(new Error(`Failed to get witness session by ID: ${request.error?.message}`))
      }
    })
  }

  private async updateBookResumeDataForWitness(session: WitnessResumeSession): Promise<void> {
    // This would update the book's resume data when a witness session is updated
    // Implementation depends on how we want to aggregate the data
  }

  private async updateBookResumeDataAfterDeletion(bookId: string): Promise<void> {
    // Recalculate book resume data after a session is deleted
  }

  private createMetadata() {
    return {
      key: 'global',
      total_sessions: 0,
      oldest_session: Date.now(),
      newest_session: Date.now(),
      total_storage_size: 0,
      cleanup_last_run: Date.now()
    }
  }

  private async updateMetadata(): Promise<void> {
    await this.initialize()
    if (!this.db) throw new Error('Database not initialized')

    const metadata = this.createMetadata()

    await runLedgerSafePostgresPrimaryWrite({
      mutations: [{
        action: 'put',
        databaseName: RESUME_DB_NAME,
        storeName: 'resume_metadata',
        key: metadata.key,
        value: metadata,
      }],
      applyCache: () => new Promise<void>((resolve, reject) => {
        const transaction = this.db!.transaction(['resume_metadata'], 'readwrite')
        transaction.objectStore('resume_metadata').put(metadata)
        transaction.oncomplete = () => resolve()
        transaction.onerror = () => reject(
          transaction.error || new Error('Resume metadata write failed'),
        )
        transaction.onabort = () => reject(
          transaction.error || new Error('Resume metadata write aborted'),
        )
      }),
    })
  }

  private async logEvent(event: ResumeEvent): Promise<void> {
    await this.initialize()
    if (!this.db) throw new Error('Database not initialized')

    await runLedgerSafePostgresPrimaryWrite({
      mutations: [{
        action: 'put',
        databaseName: RESUME_DB_NAME,
        storeName: 'resume_events',
        key: indexedDbKeyFromRecord(
          event,
          RESUME_DB_STORE_KEY_PATHS.resume_events,
        ),
        value: event,
      }],
      applyCache: () => new Promise<void>((resolve, reject) => {
        const transaction = this.db!.transaction(['resume_events'], 'readwrite')
        transaction.objectStore('resume_events').add(event)
        transaction.oncomplete = () => resolve()
        transaction.onerror = () => reject(
          transaction.error || new Error('Resume event write failed'),
        )
        transaction.onabort = () => reject(
          transaction.error || new Error('Resume event write aborted'),
        )
      }),
    })
  }

  private startPeriodicCleanup(): void {
    this.cleanupInterval = setInterval(async () => {
      try {
        await this.cleanupExpiredSessions()
      } catch (error) {
        console.error('Periodic cleanup failed:', error)
      }
    }, CLEANUP_INTERVAL_HOURS * 60 * 60 * 1000)
  }

  // ============================================================================
  // Public API
  // ============================================================================

  async destroy(): Promise<void> {
    assertLedgerSafePostgresPrimaryOperationSupported(
      'Destroying SegmentationResumeDB',
    )
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval)
    }

    if (this.db) {
      this.db.close()
      this.db = null
    }

    this.isInitialized = false
  }

  async exportSessions(): Promise<Blob> {
    const allBooks = await this.getAllBooksWithResumeSessions()
    const allWitnessSessions = await this.getAllWitnessSessions()
    const allBatchSessions = await this.getAllBatchSessions()

    const exportData = {
      books: allBooks,
      witnessSessions: allWitnessSessions,
      batchSessions: allBatchSessions,
      exportedAt: new Date().toISOString(),
      version: DB_VERSION
    }

    return new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' })
  }

  async exportAllData(): Promise<Record<string, any[]>> {
    await this.initialize()
    if (!this.db) throw new Error('Database not initialized')
    const transaction = this.db.transaction(RESUME_DB_STORE_NAMES, 'readonly')

    return new Promise((resolve, reject) => {
      const snapshot: Record<string, any[]> = {}
      transaction.oncomplete = () => resolve(snapshot)
      transaction.onerror = () => reject(transaction.error || new Error('Resume database export transaction failed'))
      transaction.onabort = () => reject(transaction.error || new Error('Resume database export transaction aborted'))

      for (const store of RESUME_DB_STORE_NAMES) {
        const request = transaction.objectStore(store).getAll()
        request.onsuccess = () => {
          snapshot[store] = request.result
        }
      }
    })
  }

  async replaceAllData(snapshot: Partial<Record<ResumeDbStoreName, any[]>>): Promise<void> {
    assertLedgerSafePostgresPrimaryOperationSupported(
      'Replacing the complete SegmentationResumeDB cache',
    )
    await this.initialize()
    if (!this.db) throw new Error('Database not initialized')
    const transaction = this.db.transaction(RESUME_DB_STORE_NAMES, 'readwrite')

    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => resolve()
      transaction.onerror = () => reject(transaction.error || new Error('Resume database replacement transaction failed'))
      transaction.onabort = () => reject(transaction.error || new Error('Resume database replacement transaction aborted'))

      try {
        for (const store of RESUME_DB_STORE_NAMES) {
          const objectStore = transaction.objectStore(store)
          objectStore.clear()

          const records = snapshot[store]
          if (!Array.isArray(records)) {
            continue
          }
          for (const record of records) {
            objectStore.put(record)
          }
        }
      } catch (error) {
        transaction.abort()
        reject(error)
      }
    })
  }

  private async getAllWitnessSessions(): Promise<WitnessResumeSession[]> {
    await this.initialize()
    if (!this.db) throw new Error('Database not initialized')

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['witness_sessions'], 'readonly')
      const store = transaction.objectStore('witness_sessions')
      const request = store.getAll()

      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
  }

  private async getAllBatchSessions(): Promise<BatchResumeSession[]> {
    await this.initialize()
    if (!this.db) throw new Error('Database not initialized')

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['batch_sessions'], 'readonly')
      const store = transaction.objectStore('batch_sessions')
      const request = store.getAll()

      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
  }

  private async getAllFromStore(storeName: ResumeDbStoreName): Promise<any[]> {
    await this.initialize()
    if (!this.db) throw new Error('Database not initialized')

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([storeName], 'readonly')
      const store = transaction.objectStore(storeName)
      const request = store.getAll()

      request.onsuccess = () => resolve(request.result || [])
      request.onerror = () => reject(request.error)
    })
  }

  private async clearStore(storeName: ResumeDbStoreName): Promise<void> {
    assertLedgerSafePostgresPrimaryOperationSupported(
      `Clearing SegmentationResumeDB.${storeName}`,
    )
    await this.initialize()
    if (!this.db) throw new Error('Database not initialized')

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([storeName], 'readwrite')
      const store = transaction.objectStore(storeName)
      const request = store.clear()

      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }

  private async putManyToStore(storeName: ResumeDbStoreName, records: any[]): Promise<void> {
    assertLedgerSafePostgresPrimaryOperationSupported(
      `Bulk replacing SegmentationResumeDB.${storeName}`,
    )
    await this.initialize()
    if (!this.db) throw new Error('Database not initialized')

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([storeName], 'readwrite')
      const store = transaction.objectStore(storeName)

      if (records.length === 0) {
        resolve()
        return
      }

      let completed = 0
      records.forEach(record => {
        const request = store.put(record)
        request.onsuccess = () => {
          completed += 1
          if (completed === records.length) {
            resolve()
          }
        }
        request.onerror = () => reject(request.error)
      })
    })
  }
}

// ============================================================================
// Singleton Instance
// ============================================================================

export const resumeStorage = new HierarchicalResumeStorage()

// Initialize on import
if (!isTestEnvironment) {
  resumeStorage.initialize().catch(error => {
    console.error('Failed to initialize resume storage:', error)
  })
}
